<template>
  <div class="al-page">
    <!-- page header rail, same construction as the alerts dashboard's: title
         left, page-level actions right. replaces the old blue gradient banner
         whose absolutely-positioned title overlapped the date controls. -->
    <div class="al-rail al-rail--flush">
      <h3 class="al-rail-title">Alert History <FiFavStar /></h3>
      <div class="al-rail-actions">
        <!-- the Issues filter, mobile only: the refine row has no width to
             spare there, and this is the one filter someone opens the page
             for. icon + count, no word -- the title carries the rest. -->
        <button
          v-if="mobileView && (issues_cnt > 0 || needs_attention)"
          type="button"
          class="al-chip al-fc-red al-rail-issues"
          :class="{ 'al-chip--active': needs_attention }"
          :aria-pressed="needs_attention ? 'true' : 'false'"
          aria-label="Show only alerts with a failed SMS or a bounced/complaint email"
          title="Show only alerts with a failed SMS or a bounced/complaint email"
          @click="needs_attention = !needs_attention"
        ><i class="fa fa-exclamation-triangle" aria-hidden="true"></i> {{ issues_cnt }}</button>
        <!-- the filter row's download, mobile only: it exports what the
             filters show, so it reads fine from the header and costs the row
             nothing -->
        <b-button
          v-if="mobileView"
          size="sm"
          variant="success"
          :disabled="loading"
          aria-label="Download filtered results"
          title="Download filtered results"
          @click="download"
        ><i class="fa fa-download" aria-hidden="true"></i></b-button>
        <b-button
          v-if="showEnableAlertsPackageButton"
          size="sm"
          variant="primary"
          :to="{ name: 'alerts_dashboard', query: { upsell: '1' } }"
        >
          <b>Enable Alerts Package</b>
        </b-button>
        <b-button
          v-if="user.features[user.tenant] && user.features[user.tenant].alerts_enabled && (user.role === 'admin' || user.role === 'customer-admin' || user.role === 'dispatch' || user.role === 'manager')"
          size="sm"
          variant="primary"
          :to="{ name: user.types[user.tenant] === 'TBN' ? 'group_message_sender_tbn' : 'group_message_sender' }"
          aria-label="Group Message"
          title="Group Message"
        >
          <i class="fa fa-bullhorn"></i>
        </b-button>
        <b-button size="sm" variant="success" @click="showEmailDeliveryInfoModal = true" aria-label="more info" title="more info"><i class="fa fa-info-circle"></i></b-button>
      </div>
    </div>

    <b-modal v-model="showEmailDeliveryInfoModal" title="Alert History Help" size="lg" ok-only ok-title="Close" scrollable>
      <div class="text-left">
        <h4>Using the Alert History page</h4>
        <ul>
          <li>Pick a quick <b>range</b> chip, set a bespoke <b>From</b>/<b>To</b> window (or use the arrow buttons to shift day by day), and pick a <b>type</b> from the dropdown. Results reload automatically as you change them.</li>
          <li>Use the <b>filter box</b> on the stats line just above the results to search the loaded rows by driver, vehicle, recipient, or message text without reloading from the server.</li>
          <li>Click the colored <b>type chips</b> in the filter row to filter by one or more alert types at once; click a selected chip again to remove it.</li>
          <li>Click the <b>Issues</b> chip to show just the alerts with a failed SMS or a bounced/complaint email. It shows how many such alerts the current filters contain, and only appears when there is at least one.</li>
          <li>The <b>Recipients</b> column lists everyone an alert went to in one place, with each line tagged by what it is: <b>Name</b>, <b>Driver</b>, <b>Customer</b>, <b>Other</b>, <b>Voice</b>, or <b>Email</b>. Only the tags an alert actually used are shown, so the tags differ by alert type.</li>
          <li>Click the <b>download</b> icon at the right of the date controls to export the current filtered results to a spreadsheet.</li>
          <li>Click the <b>expand</b> icon on a row to expand the full message text and delivery details. :role[fleet-admin] can delete an individual alert record with the red <b>x</b> in the actions column.</li>
          <li>Fleet admins can click the <b>copy</b> icon in the actions column to copy that row's alert ID (movement_vehicle_id) to the clipboard.</li>
        </ul>

        <hr/>

        <h4>Using alert feedback</h4>
        <p>
          You can flag a concern on each alert for your team to follow up on, and support is
          notified automatically to help investigate. Look for the <b>flag</b> icon in the
          actions column on any alert row.
        </p>
        <ul>
          <li><b>Flag</b>: record a concern. Click the flag, describe the issue in the comment box that opens next to it, then submit (a comment is required). Click the flag again to review or update the concern.</li>
        </ul>
        <template v-if="feedback_audit_mode_enabled">
          <p>Feedback audit mode is enabled for your organization, which adds:</p>
          <ul>
            <li><b>Thumbs up</b>: mark the alert as correct or expected. Click again to clear your rating.</li>
            <li><b>Thumbs down</b>: mark the alert as incorrect or unexpected. Click again to clear.</li>
            <li>Use the <b>Feedback</b> filter chips above the table to show only alerts with a given rating. Hover each chip for a short description. Click a chip to filter; click the same chip again to show all rows.</li>
            <li>The <b>&times;</b> button on the Feedback filters clears every rating and comment for all alerts in the current date range (you will be asked to confirm).</li>
          </ul>
        </template>
        <p v-else class="text-muted" style="font-size: 13px;">
          Feedback audit mode is off for your organization, so only the flag is shown. A fleet admin can turn it on under Settings &gt; General.
        </p>

        <hr/>

        <h4>Email Delivery Status</h4>
        <div style="white-space: pre-line;" v-html="emailDeliveryInfoText"></div>
      </div>
    </b-modal>

    <div v-if="singleAlertFilter" class="al-single-alert-banner">
      Showing 1 alert record &nbsp;<a href="#" @click.prevent="clearSingleAlertFilter">Clear filter</a>
    </div>

    <!-- feedback rating chips, only when audit mode is on (the per-type count
         chips live in the consolidated filter row below) -->
    <div v-if="feedback_audit_mode_enabled" class="al-chip-filters">
      <div class="al-chip-group" role="group" aria-label="Feedback filters">
        <span class="al-chip-group-label" :title="feedbackFilterTooltips.group">Feedback</span>
        <button
          type="button"
          class="al-chip al-fc-green"
          :class="{ 'al-chip--active': selected_feedback == 'positive' }"
          :aria-pressed="selected_feedback == 'positive' ? 'true' : 'false'"
          :title="feedbackFilterTooltips.positive"
          @click="filter_by_feedback('positive')"
        ><i class="fa fa-check" aria-hidden="true"></i> {{ feedback_positive_cnt }}</button>
        <button
          type="button"
          class="al-chip al-fc-red"
          :class="{ 'al-chip--active': selected_feedback == 'negative' }"
          :aria-pressed="selected_feedback == 'negative' ? 'true' : 'false'"
          :title="feedbackFilterTooltips.negative"
          @click="filter_by_feedback('negative')"
        ><i class="fa fa-thumbs-down" aria-hidden="true"></i> {{ feedback_negative_cnt }}</button>
        <button
          type="button"
          class="al-chip al-fc-amber"
          :class="{ 'al-chip--active': selected_feedback == 'concern' }"
          :aria-pressed="selected_feedback == 'concern' ? 'true' : 'false'"
          :title="feedbackFilterTooltips.concern"
          @click="filter_by_feedback('concern')"
        ><i class="fa fa-flag" aria-hidden="true"></i> {{ feedback_concern_cnt }}</button>
        <button
          type="button"
          class="al-chip al-fc-gray"
          :class="{ 'al-chip--active': selected_feedback == 'none' }"
          :aria-pressed="selected_feedback == 'none' ? 'true' : 'false'"
          :title="feedbackFilterTooltips.none"
          @click="filter_by_feedback('none')"
        ><i class="fa fa-circle-o" aria-hidden="true"></i> {{ feedback_none_cnt }}</button>
        <button
          type="button"
          class="al-chip"
          :disabled="clearing_feedback"
          :title="feedbackFilterTooltips.clear"
          aria-label="Clear all feedback for this date range"
          @click="confirm_clear_feedback_for_date_range"
        ><i class="fa fa-times" aria-hidden="true"></i></button>
      </div>
    </div>

    <!-- server-side window (range chips, a bespoke From/To, the type select)
         with the download pinned to the far right. everything auto-applies,
         so the old explicit Search button is gone. -->
    <div class="al-filter-row">
      <div class="al-chip-group" role="group" aria-label="Alerts date range">
        <span class="al-chip-group-label">Range</span>
        <button
          v-for="option in dateRangeOptions"
          :key="option"
          type="button"
          class="al-chip"
          :class="{ 'al-chip--active': isActiveRange(option) }"
          :aria-pressed="isActiveRange(option) ? 'true' : 'false'"
          :disabled="loading"
          @click="set_days_range(option)"
        >{{ option }}d</button>
        <!-- phone-only, sitting after the last range chip: the From/To pickers
             and their four shift arrows are the widest thing on this page and
             cost two lines in mobile mode, so there they collapse behind this
             and the row keeps to the chips. it reads as active (and the dates open
             on their own) whenever the window isn't one of the chips above --
             a bespoke window must never look like no selection at all. -->
        <button
          type="button"
          class="al-chip al-chip--custom"
          :class="{ 'al-chip--active': datesOpen }"
          :aria-pressed="datesOpen ? 'true' : 'false'"
          :aria-expanded="datesOpen ? 'true' : 'false'"
          aria-controls="al-filter-dates"
          aria-label="Set a specific From / To window"
          title="Set a specific From / To window"
          @click="show_custom_dates = !show_custom_dates"
        >&hellip;</button>
      </div>
      <!-- back arrows flank the from date, forward arrows the to date; the
           cluster travels as one block so a tight row never orphans the arrows -->
      <div
        id="al-filter-dates"
        class="al-filter-dates"
        :class="{ 'al-filter-dates--open': datesOpen }"
      >
        <span class="al-filter-dateunit">
          <b-button-group class="toolbar-date-shifts">
            <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn" title="Shift back" v-on:click.prevent="shift_detail_graph_date(-selectedDayCount)"><i class="fa fa-angle-double-left" aria-hidden="true"></i></b-button>
            <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn" title="Previous day" v-on:click.prevent="shift_detail_graph_date(-1)"><i class="fa fa-angle-left" aria-hidden="true"></i></b-button>
          </b-button-group>
          <span class="al-filter-field">
            <span class="al-chip-group-label">From</span>
            <b-form-input v-model="fromDateIso" type="date" size="sm" class="al-filter-input" @change="load_data()"/>
          </span>
        </span>
        <span class="al-filter-dateunit">
          <span class="al-filter-field">
            <span class="al-chip-group-label">To</span>
            <b-form-input v-model="toDateIso" type="date" size="sm" class="al-filter-input" @change="load_data()"/>
          </span>
          <b-button-group class="toolbar-date-shifts">
            <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn" title="Next day" v-on:click.prevent="shift_detail_graph_date(1)"><i class="fa fa-angle-right" aria-hidden="true"></i></b-button>
            <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn" title="Shift forward" v-on:click.prevent="shift_detail_graph_date(selectedDayCount)"><i class="fa fa-angle-double-right" aria-hidden="true"></i></b-button>
          </b-button-group>
        </span>
      </div>
      <!-- in mobile mode this rides the range line rather than claiming one of
           its own: the label drops (the select's own "All Types" says what it
           is) and the control flexes into whatever the chips leave -->
      <span class="al-filter-field al-filter-field--type">
        <span class="al-chip-group-label">Type</span>
        <b-form-select size="sm" class="al-filter-select al-filter-select--wide" v-model="alert_type" :options="type_options" @change="load_data()"/>
      </span>
      <!-- exports what the filters currently show, so it lives with them --
           except in mobile mode, where it moves to the header rail (see
           al-rail-actions) and gives the row back its width -->
      <b-button v-if="!mobileView" size="sm" variant="success" class="al-download" :disabled="loading" @click="download" aria-label="Download" title="Download filtered results"><i class="fa fa-download" aria-hidden="true"></i></b-button>
    </div>

    <!-- client-side refinements over the loaded rows: per-type count chips,
         the Issues toggle, and the confirmed select -->
    <div v-if="hasTypeChips || issues_cnt > 0 || needs_attention || driver_confirmation_enabled" class="al-filter-row al-filter-row--refine">
      <div v-if="hasTypeChips" class="al-chip-group al-chip-group--flow" role="group" aria-label="Alert type filters">
        <span class="al-chip-group-label">Types</span>
        <button
          v-for="(value, key) in summary.type_unit_counts"
          :key="key"
          type="button"
          class="al-chip al-chip--type"
          :class="{ 'al-chip--active': selected_types.indexOf(key) > -1 }"
          :style="typeChipStyle(key)"
          :aria-pressed="selected_types.indexOf(key) > -1 ? 'true' : 'false'"
          @click="filter_by_type(key)"
        >{{ alertTypeLabel(key) }} ({{ value.count }})</button>
      </div>
      <!-- only appears when the loaded rows contain delivery problems; stays
           visible while active so an emptied result can still be untoggled.
           in mobile mode it moves into the header rail as an icon chip (see
           al-rail-actions), where it costs no width in the filter zone. -->
      <button
        v-if="!mobileView && (issues_cnt > 0 || needs_attention)"
        type="button"
        class="al-chip al-fc-red"
        :class="{ 'al-chip--active': needs_attention }"
        :aria-pressed="needs_attention ? 'true' : 'false'"
        title="Show only alerts with a failed SMS or a bounced/complaint email"
        @click="needs_attention = !needs_attention"
      >Issues ({{ issues_cnt }})</button>
      <span v-if="driver_confirmation_enabled" class="al-filter-field">
        <span class="al-chip-group-label">Confirmed</span>
        <b-form-select size="sm" class="al-filter-select" v-model="confirmed_state" :options="confirmed_options"/>
      </span>
    </div>

    <!-- results meta line replacing the old grey summary boxes: filtered/total
         plus the channel totals, in the vsm board's results-count voice; the
         base select and search pill — the client-side refinements applied
         after load — ride its far right, next to the rows they narrow -->
    <div v-if="!loading && summary.total_cnt > 0" class="al-results-meta">
      <!-- the counts travel as one group: in mobile mode they become a single
           sideways-scrolling line rather than wrapping to three, and the base
           select and search pill drop below them -->
      <span class="al-results-stats">
        <span class="al-results-count">{{ items.length }} of {{ summary.total_cnt }} alerts</span>
        <span v-if="summary.email_cnt > 0" class="al-results-stat">{{ summary.email_cnt }} emails</span>
        <span v-if="summary.sms_cnt > 0" class="al-results-stat">{{ summary.sms_cnt }} SMS (${{ summary.sms_cost }})</span>
        <span v-if="summary.voice_cnt > 0" class="al-results-stat">{{ summary.voice_cnt }} voice</span>
        <span v-if="summary.sms_inbound_cnt > 0" class="al-results-stat">{{ summary.sms_inbound_cnt }} SMS received (${{ summary.sms_inbound_cost }})</span>
      </span>
      <span v-if="base_options && base_options.length > 2" class="al-filter-field al-meta-base">
        <span class="al-chip-group-label">Base</span>
        <b-form-select size="sm" class="al-filter-select" v-model="selected_base" :options="base_options"/>
      </span>
      <span class="al-search">
        <i class="fa fa-search al-search-icon" aria-hidden="true"></i>
        <input
          v-model="alert_filter"
          type="text"
          class="al-search-input"
          placeholder="Filter results"
          aria-label="Filter loaded results by driver, vehicle, recipient, or message text"
        />
        <button v-if="alert_filter" type="button" class="al-search-clear" aria-label="Clear filter" @click="alert_filter = ''"><i class="fa fa-times" aria-hidden="true"></i></button>
      </span>
    </div>

    <div class="al-table-wrap">
      <b-table v-if="!mobileView" :busy="loading" sticky-header="70vh" bordered :items="items" :fields="fields"
               :current-page="currentPage" :tbody-tr-class="get_row_color"
               :per-page="perPage">

        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>

        <template slot="alert_type" slot-scope="row">
          <span :style="typeTextStyle(row.item.alert_type)">{{alertTypeLabel(row.item.alert_type)}}</span>
        </template>

        <template slot="booking_id" slot-scope="row">
          <router-link
            class="al-booking-id"
            :to="{ path: actualsPath + '?booking_id=' + row.item.booking_hash }"
            :title="'Open actuals for ' + row.item.booking_id"
          >
            <span class="al-booking-id-primary">{{ bookingIdParts(row.item).primary }}</span>
            <span v-if="bookingIdParts(row.item).movement" class="al-booking-id-mvmt">
              mvmt {{ bookingIdParts(row.item).movement }}
            </span>
          </router-link>
        </template>

        <template slot="vehicle_id" slot-scope="row">
          <div>
            <span v-for="(id, idx) in vehicleIds(row.item)" :key="id">
              <router-link
                class="font-weight-bold"
                :to="{ path: '/vehicle_trip_history', query: { vehicleId: id } }"
              >{{ id }}</router-link><span v-if="idx < vehicleIds(row.item).length - 1">, </span>
            </span>
          </div>
        </template>

        <template slot="processed_time" slot-scope="row">
          {{row.item.processed_time | formatDateTimeSmall}}
          <span v-if="row.item.movement_vehicle_id.includes('reprocess')">(reprocessed)</span>
        </template>

        <template slot="confirmed_time" slot-scope="row">
          {{row.item.confirmed_time | formatDateTimeSmall}}
        </template>

        <template slot="start_datetime" slot-scope="row">
          {{row.item.start_datetime | formatDateTimeSmall}}
        </template>

        <template slot="message" slot-scope="row">
          <div class="al-message">
            <div
              v-for="part in messageParts(row.item)"
              :key="part.key"
              class="al-message-part"
            >
              <span class="al-tag" :class="'al-tag-' + part.key">{{ part.label }}</span>
              <span class="al-message-body">
                <span v-if="part.html" class="al-message-text al-message-mono" v-html="part.html"/>
                <span v-else class="al-message-text">{{ part.text }}</span>
                <a
                  v-if="part.truncated"
                  href="#"
                  class="al-message-more"
                  @click.prevent="row.toggleDetails"
                >{{ row.detailsShowing ? 'show less' : 'show more' }}</a>
              </span>
            </div>
          </div>
        </template>

        <template slot="recipients" slot-scope="row">
          <div class="al-recipients">
            <div
              v-for="group in recipientGroups(row.item)"
              :key="group.key"
              class="al-recipient-group"
            >
              <span class="al-tag" :class="'al-tag-' + group.key">{{ group.label }}</span>
              <span class="al-recipient-values" :class="{ 'al-recipient-values-scroll': group.values.length > 3 }">
                <span v-for="val in group.values" :key="val" class="al-recipient-value">{{ val }}</span>
              </span>
            </div>
          </div>
        </template>

        <template slot="provider_response" slot-scope="row">
          <div class="al-delivery">
            <div
              v-for="status in deliveryStatuses(row.item)"
              :key="status.key"
              class="al-delivery-row"
            >
              <span class="al-tag" :class="'al-tag-' + status.key">{{ status.label }}</span>
              <span class="al-delivery-body">
                <span class="al-delivery-text" :class="'al-delivery-' + status.state">{{ status.text }}</span>
                <span v-if="status.note" class="al-delivery-note">{{ status.note }}</span>
                <span v-if="status.datetime" class="al-delivery-note">{{ status.datetime | formatDateTimeSmall }}</span>
              </span>
            </div>
          </div>
        </template>

        <template slot="finish_datetime" slot-scope="row">
          {{row.item.finish_datetime | formatDateTimeSmall}}
        </template>

        <template slot="details" slot-scope="row">
          <b-button size="sm" @click="row.toggleDetails" :title="row.detailsShowing ? 'Collapse' : 'Expand'">
            <i :class="row.detailsShowing ? 'fa fa-compress' : 'fa fa-expand'" aria-hidden="true"></i>
          </b-button>
        </template>

        <template slot="actions" slot-scope="row">
          <div class="al-actions-cell">
            <alert-feedback-controls :item="row.item" :tenant="user.tenant" :audit-mode="feedback_audit_mode_enabled" @showAlert="(msg, variant, ms) => $emit('showAlert', msg, variant, ms)" />
            <span
              v-if="row.item.alert_type == 'CustomerTripNotification'"
              title="Reprocess alert"
              style="cursor: pointer;"
              @click="reprocess_alert(row.item)"
            ><span style="font-size: 24px;">&#8635;</span></span>
            <a
              v-if="isEffectiveAdmin"
              href="#"
              @click.prevent="copy_alert_id(row.item)"
              :title="copied_alert_id === row.item.movement_vehicle_id ? 'Copied!' : ('Copy Alert ID: ' + row.item.movement_vehicle_id)"
            >
              <i :class="copied_alert_id === row.item.movement_vehicle_id ? 'fa fa-check' : 'fa fa-copy'" aria-hidden="true"></i>
            </a>
            <span
              v-if="isEffectiveAdmin"
              title="Delete alert record"
              style="font-size: 20px; font-weight: bold; color: #b30000; cursor: pointer;"
              @click="delete_alert_record(row.item)"
            >x</span>
          </div>
        </template>

        <template slot="row-details" slot-scope="row">
          <b-card style="text-align: left;">
            <alert-detail-body :item="row.item" :body-label="messageBodyLabel(row.item)" />
          </b-card>
        </template>
      </b-table>

      <!-- mobile card layout, same construction as the vsm board's card list:
           a twelve-column table cannot be shrunk below ~900px without either
           a sideways scroll that hides the message or cells too narrow to
           read, so each alert becomes a card with the same reading order the
           table has (identity, type + time, delivery, recipients, message)
           and the rest one tap away. -->
      <div v-else class="al-card-list">
        <div v-if="loading" class="al-card-busy">
          <b-spinner small></b-spinner>&nbsp;Loading alerts...
        </div>
        <div v-else-if="!items.length" class="al-card-empty">No alerts match the current filters.</div>
        <div
          v-for="item in pagedItems"
          :key="item.movement_vehicle_id"
          class="al-acard"
          :class="{ 'al-acard--issue': hasDeliveryIssue(item) }"
        >
          <!-- header: type chip carries its own hue (the same one the table
               column uses), the time closes the line -->
          <div class="al-acard-head">
            <span class="al-acard-type" :style="typeChipStyle(item.alert_type)">{{ alertTypeLabel(item.alert_type) }}</span>
            <span class="al-acard-time">
              {{ item.processed_time | formatDateTimeSmall }}
              <span v-if="(item.movement_vehicle_id || '').includes('reprocess')" class="al-acard-reprocessed">reprocessed</span>
            </span>
          </div>

          <!-- identity line: the three columns that answer "which trip is
               this", each still a link to where its column linked -->
          <div class="al-acard-ident">
            <router-link
              class="al-acard-booking"
              :to="{ path: actualsPath + '?booking_id=' + item.booking_hash }"
              :title="'Open actuals for ' + item.booking_id"
            >
              {{ bookingIdParts(item).primary }}
              <span v-if="bookingIdParts(item).movement" class="al-booking-id-mvmt">mvmt {{ bookingIdParts(item).movement }}</span>
            </router-link>
            <span v-if="vehicleIds(item).length" class="al-acard-vehicles">
              <i class="fa fa-bus al-acard-ident-icon" aria-hidden="true" title="Vehicle"></i>
              <span v-for="(id, idx) in vehicleIds(item)" :key="id">
                <router-link :to="{ path: '/vehicle_trip_history', query: { vehicleId: id } }">{{ id }}</router-link><span v-if="idx < vehicleIds(item).length - 1">, </span>
              </span>
            </span>
            <span v-if="item.start_base" class="al-acard-base">
              <i class="fa fa-map-marker al-acard-ident-icon" aria-hidden="true" title="Base"></i>{{ item.start_base }}
            </span>
            <span v-if="item.confirmed_time" class="al-acard-confirmed">
              <i class="fa fa-check al-acard-ident-icon" aria-hidden="true" title="Confirmed"></i>{{ item.confirmed_time | formatDateTimeSmall }}
            </span>
          </div>

          <!-- delivery + recipients + message reuse the row markup's classes,
               so a failed SMS reads the same red here as in the table -->
          <div v-if="deliveryStatuses(item).length" class="al-delivery al-acard-section">
            <div v-for="status in deliveryStatuses(item)" :key="status.key" class="al-delivery-row">
              <span class="al-tag" :class="'al-tag-' + status.key">{{ status.label }}</span>
              <span class="al-delivery-body">
                <span class="al-delivery-text" :class="'al-delivery-' + status.state">{{ status.text }}</span>
                <span v-if="status.note" class="al-delivery-note">{{ status.note }}</span>
                <span v-if="status.datetime" class="al-delivery-note">{{ status.datetime | formatDateTimeSmall }}</span>
              </span>
            </div>
          </div>

          <div v-if="recipientGroups(item).length" class="al-recipients al-acard-section">
            <div v-for="group in recipientGroups(item)" :key="group.key" class="al-recipient-group">
              <span class="al-tag" :class="'al-tag-' + group.key">{{ group.label }}</span>
              <span class="al-recipient-values" :class="{ 'al-recipient-values-scroll': group.values.length > 3 }">
                <span v-for="val in group.values" :key="val" class="al-recipient-value">{{ val }}</span>
              </span>
            </div>
          </div>

          <div
            class="al-message al-acard-section al-acard-message"
            :class="{ 'al-acard-message--clamped': !isCardExpanded(item) }"
          >
            <div v-for="part in messageParts(item)" :key="part.key" class="al-message-part">
              <span class="al-tag" :class="'al-tag-' + part.key">{{ part.label }}</span>
              <span class="al-message-body">
                <span v-if="part.html" class="al-message-text al-message-mono" v-html="part.html"/>
                <span v-else class="al-message-text">{{ part.text }}</span>
              </span>
            </div>
          </div>

          <div v-if="isCardExpanded(item)" class="al-acard-details">
            <alert-detail-body :item="item" :body-label="messageBodyLabel(item)" />
          </div>

          <!-- footer: the actions column's controls, plus the expander that
               stands in for the table's details toggle. they share one line
               with the expander, so the card face costs nothing to keep them
               on every card rather than only an expanded one. -->
          <div class="al-acard-foot">
            <alert-feedback-controls :item="item" :tenant="user.tenant" :audit-mode="feedback_audit_mode_enabled" @showAlert="(msg, variant, ms) => $emit('showAlert', msg, variant, ms)" />
            <span
              v-if="item.alert_type == 'CustomerTripNotification'"
              class="al-acard-action"
              title="Reprocess alert"
              @click="reprocess_alert(item)"
            >&#8635;</span>
            <a
              v-if="isEffectiveAdmin"
              href="#"
              class="al-acard-action"
              @click.prevent="copy_alert_id(item)"
              :title="copied_alert_id === item.movement_vehicle_id ? 'Copied!' : ('Copy Alert ID: ' + item.movement_vehicle_id)"
            >
              <i :class="copied_alert_id === item.movement_vehicle_id ? 'fa fa-check' : 'fa fa-copy'" aria-hidden="true"></i>
            </a>
            <span
              v-if="isEffectiveAdmin"
              class="al-acard-action al-acard-action--danger"
              title="Delete alert record"
              @click="delete_alert_record(item)"
            >x</span>
            <button
              type="button"
              class="al-acard-expand"
              :aria-expanded="isCardExpanded(item) ? 'true' : 'false'"
              @click="toggle_card_details(item)"
            >
              <i :class="isCardExpanded(item) ? 'fa fa-compress' : 'fa fa-expand'" aria-hidden="true"></i>
              {{ isCardExpanded(item) ? 'Less' : 'Details' }}
            </button>
          </div>
        </div>
      </div>

      <b-row>
        <b-col>
          <b-pagination
              v-model="currentPage"
              :total-rows="items.length"
              :per-page="perPage"
              align="center"
          >
          </b-pagination>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>

import {api_url, clear_alert_feedback_for_date_range, delete_alert, get_alerts, reprocess_alerts_for_booking} from '../networking/apis'
import {mapState} from "vuex";
import moment from 'moment'
import {justDate} from "@/helpers/dates";
import axios from 'axios';
import { hasAlertsAccess } from '../utils/alerts_access'
import { hasVsmAccess } from '../utils/vsm_access'
import alert_feedback_controls from '@/components/alert_feedback_controls'
import alert_detail_body from '@/components/alert_detail_body'
import { isFeedbackAuditModeEnabled } from '@/helpers/feedback_audit_mode'
import { countAlertFeedback, matchesAlertFeedbackFilter, ALERT_FEEDBACK_FILTER_TOOLTIPS } from '@/helpers/alert_feedback_filter'
import { copyToClipboard } from '@/helpers/vsm_standalone_url'

// alert type is an open-ended set, so colors are assigned by hashing the value
// into this fixed palette rather than a static per-value map. softened tones +
// white text: all sit at ~4.7:1 contrast on white so the chips read evenly and
// none shouts louder than another. two constraints on the hues: red/crimson is
// skipped entirely (red is the .al-chip-selected border color, and a red chip
// would blur the selected/unselected distinction), and hues are spaced far
// enough apart that neighboring entries stay tellable apart — one brown, one
// gold, one olive, and so on, rather than several muddy near-duplicates.
const TYPE_CHIP_COLOR_PALETTE = [
  '#a06726', '#857317', '#4d812b', '#29834f',
  '#258079', '#2b7ba3', '#5070c8', '#8262c2',
  '#a650b8', '#bb4891', '#6f747b',
]

// midnight twin of the palette above. the light hues are tuned to ~4.7:1 on
// WHITE, which lands them at ~3.0:1 once the page goes dark -- and because they
// arrive as inline styles, css cannot lift them without an !important that
// would flatten the per-type color coding. so each hue is carried over at the
// same hue angle, very slightly desaturated, with its lightness raised until it
// clears 5:1 on the card surface (5.0-5.2:1 there, 5.6-5.9:1 on the sunken
// canvas). same order as the light palette, so a given alert type keeps its
// own color in both themes.
const TYPE_CHIP_COLOR_PALETTE_MIDNIGHT = [
  '#c78437', '#ab9524', '#65a33c', '#3aa567',
  '#35a39a', '#459bc7', '#7b91d0', '#a08acd',
  '#ba7fc7', '#ca7cad', '#8f9399',
]

// quick date ranges in the header, matching the Alerts Dashboard
const DATE_RANGE_OPTIONS = [1, 7, 30]

// how much of a long body the Message column previews before it offers to
// expand the row (the row details hold the full, untrimmed text)
const MESSAGE_PREVIEW_LIMIT = 400
const VOICE_PREVIEW_LIMIT = 300

// start of the quoted original inside a reply: the "From:" header every mail
// client writes above it, plus the two dividers that can precede that header
const QUOTED_THREAD_START = /^[ \t>]*(from:|-{3,} ?original message|_{10,})/im

// the Recipients column, in render order: which item field feeds each labeled
// group. 'name' is the recipient's name, the rest are phone numbers / addresses.
const RECIPIENT_GROUP_DEFS = [
  { key: 'name', label: 'Name', field: 'sms_name' },
  { key: 'driver', label: 'Driver', field: 'sms_drivers' },
  { key: 'customer', label: 'Customer', field: 'sms_leaders' },
  { key: 'other', label: 'Other', field: 'sms' },
  { key: 'voice', label: 'Voice', field: 'voice_number' },
  { key: 'email', label: 'Email', field: 'email' },
]

// these groups carry no meaning for the given alert type — they used to be
// dropped as whole columns in check_cols(), now they're dropped from the cell.
const RECIPIENT_GROUPS_HIDDEN_BY_TYPE = {
  SMSReceived: ['driver', 'customer'],
  SMSResponse: ['driver', 'customer'],
  GroupMessage: ['name', 'driver', 'customer'],
  DriverHOSAlert: ['customer'],
}

export default {
  name: 'alert_list',
  components: {
    'alert-feedback-controls': alert_feedback_controls,
    'alert-detail-body': alert_detail_body,
  },

  data() {
    return {
      msg: 'alerts',
      showEmailDeliveryInfoModal: false,
      emailDeliveryInfoText: `The different email statuses you're seeing represent the progression of an email through the delivery process:

1. <strong>Sent</strong> - This means our system successfully sent the email out. At this point, the email has left our servers and is on its way to the recipient.

2. <strong>Delivered</strong> - This means the recipient's mail server confirmed it received the email. The email has successfully arrived at the destination inbox.

3. <strong>Opened</strong> - This means the recipient actually opened the email. We detect this through a tiny invisible tracking pixel in the email - when someone opens the email and their email client loads images, we get notified.

So the typical happy path is: <strong>Sent → Delivered → Opened</strong>

<strong>Why you might see only "Sent" vs "Delivered":</strong>

"Sent" alone could mean we're still waiting for the delivery confirmation (usually happens within seconds to minutes)

Some mail servers don't send delivery confirmations back

<strong>Why "Opened" doesn't always appear:</strong>

Not all email clients load images by default (Outlook, for example, often blocks them)

If someone reads the email in a preview pane without images enabled, we won't see the "Opened" event."`,
      alert_filter: '',
      needs_attention: false,
      sms_stats_enabled: true,
      driver_confirmation_enabled: true,
      items: [],
      items_full: [],
      summary: [],
      base_options: [],
      selected_base: '',
      loading: true,
      details_fromDate: justDate(moment().add(-1, 'days')),
      details_toDate: justDate(moment()),
      // phone-only disclosure for the From/To pickers and their shift arrows
      // (see the Custom chip); desktop shows them unconditionally in CSS
      show_custom_dates: false,
      // the table gives way to a card list below this width. a media query
      // rather than a user-agent sniff, and reactive to rotation/resize --
      // same approach vsm_board.vue takes for its own card list.
      mobileView: false,
      // movement_vehicle_ids of cards showing their detail body; the table
      // branch keeps using b-table's own row-details state
      expanded_cards: [],
      alert_type: '',
      selected_types: [],
      singleAlertFilter: null,
      copied_alert_id: null,
      copied_alert_id_timer: null,
      selected_feedback: null,
      clearing_feedback: false,
      items_for_feedback_counts: [],
      items_for_issue_counts: [],
      feedbackFilterTooltips: ALERT_FEEDBACK_FILTER_TOOLTIPS,
      confirmed_state:'',
      totalRows: 1,
      currentPage: 1,
      perPage: 100,
      pages: [],
      type_options: [
        { value: '', text: 'All Types' },
        { value: 'LateAlert', text: 'Late Alerts' },
        { value: 'DriverTripNotification', text: 'Driver Trip Notifications' },
        { value: 'CustomerTripNotification', text: 'Customer Trip Notifications' },
        { value: 'CustomerPostTripNotification', text: 'Customer Post Trip Notifications' },
        { value: 'PassengerTripNotification', text: 'Passenger Trip Notifications' },
        { value: 'MissingDVIR', text: 'Missing DVIR Alert' },
        { value: 'MissingDriverConfirmations', text: 'Missing Driver Confirmations' },
        { value: 'PostTripNotification', text: 'Post Trip Notifications' },
        { value: 'VehicleActivityAlert', text: 'Vehicle Activity Alert' },
        { value: 'VehicleInactivityAlert', text: 'Vehicle Inactivity Alert' },
        { value: 'RelayNotification', text: 'Relay Notifications' },
        { value: 'DriverHOSAlert', text: 'HOS Duration Alert' },
        { value: 'UnplannedVehicleActivityAlert', text: 'Unplanned Vehicle Activity' },
        { value: 'CustomerSurvey', text: 'Customer Survey' },
        { value: 'GroupMessage', text: 'Group Message' },
        { value: 'VehicleStatusReceived', text: 'Vehicle Status' },
        { value: 'EmailReceived', text: 'Email Received' },
        { value: 'SMSReceived', text: 'SMS Received' },
        { value: 'SMSResponse', text: 'SMS Response' },
        { value: 'GatewayDisconnectAlert', text: 'Gateway Disconnect Alert' },
        { value: 'BookingStateAlert', text: 'Booking State Alert' },
      ],
      confirmed_options: [
        { value: '', text: 'All' },
        { value: 'unconfirmed', text: 'Unconfirmed' },
        { value: 'confirmed', text: 'Confirmed' },
      ],
      fields_all: [],
      fields: [
        { key: 'details', label: '', sortable: false, thStyle: { width: "20px" }},
        //{ key: 'booking_hash', label: 'Booking Ref', sortable: true, thStyle: { width: "110px" }},
        { key: 'booking_id', label: 'Booking ID', sortable: true, thStyle: { width: "110px" }},
        { key: 'vehicle_id', label: 'Vehicle', sortable: true, thStyle: { width: "110px" }},
        { key: 'start_base', label: 'Base', sortable: true, thStyle: { width: "110px" }},
        { key: 'alert_type', label: 'Alert Type', sortable: true, thStyle: { width: "120px" }},
        { key: 'processed_time', label:'Alert Time', sortable: true, thStyle: { width: "110px" }},
        { key: 'provider_response', label:'Delivery Status', sortable: true, thStyle: { width: "170px" }},
        { key: 'confirmed_time', label:'Conf Time', sortable: true, thStyle: { width: "110px" }},
        // { key: 'sms_recipients', label:'SMS #', sortable: true, thStyle: { width: "110px" }},
        // { key: 'sms_size', label:'SMS Size', sortable: true},
        // { key: 'sms_units', label:'SMS Units', sortable: true},
        // one column for every destination (name, phone numbers, email); each
        // alert type fills in only a couple of these, so separate columns left
        // most cells blank. see recipientGroups() for the per-type labeling.
        { key: 'recipients', label: 'Recipients', sortable: true, sortByFormatted: true,
          formatter: (value, key, item) => this.recipientSortKey(item), thStyle: { width: "240px" }},
        { key: 'message', label: 'Message', sortable: true, thStyle: { width: "400px" }},
        { key: 'actions', label: '', sortable: false, thStyle: { width: "90px" }},
      ]
      ,
    }
  },

  async created() {
    // CM stacks the movement id under the charter id, so the "ID" in the
    // default header would be ambiguous about which one it names
    if (this.isCmTenant)
      this.fields = this.fields.map(f => f.key === 'booking_id' ? Object.assign({}, f, { label: 'Booking' }) : f)
    this.fields_all = this.fields
    this.applyDeepLinkQuery()
    this.check_features()
  },
  mounted() {
    this.setupMobileQuery()
  },
  beforeDestroy() {
    if (this.copied_alert_id_timer)
      clearTimeout(this.copied_alert_id_timer)
    this.teardownMobileQuery()
  },
  methods: {
    // mobile breakpoint, reactive to rotation/resize so the layout is not
    // pinned at mount the way a user-agent sniff would be (vsm_board.vue)
    setupMobileQuery() {
      if (typeof window === 'undefined' || !window.matchMedia) return
      // 900px, not the vsm board's 640: this page's table carries twelve
      // columns to the board's six, so it runs out of width first. every
      // "mobile mode" rule in the style block below is keyed to the same
      // number -- change the two together or the card list arrives while the
      // filter controls are still in their desktop layout.
      this._mql = window.matchMedia('(max-width: 900px)')
      this.mobileView = this._mql.matches
      this._mqlHandler = e => { this.mobileView = e.matches }
      if (this._mql.addEventListener) this._mql.addEventListener('change', this._mqlHandler)
      else this._mql.addListener(this._mqlHandler)
    },
    teardownMobileQuery() {
      if (!this._mql) return
      if (this._mql.removeEventListener) this._mql.removeEventListener('change', this._mqlHandler)
      else this._mql.removeListener(this._mqlHandler)
    },
    isCardExpanded(item) {
      return this.expanded_cards.indexOf(item.movement_vehicle_id) > -1
    },
    toggle_card_details(item) {
      const i = this.expanded_cards.indexOf(item.movement_vehicle_id)
      if (i > -1) this.expanded_cards.splice(i, 1)
      else this.expanded_cards.push(item.movement_vehicle_id)
    },
    // the vehicle cell's own parsing, lifted out of the template so the card
    // and the table row read the same list rather than repeating the chain
    vehicleIds(item) {
      return (item.vehicle_id || '')
        .split(',')
        .map(s => s.trim())
        .filter(s => s && s.toLowerCase() !== 'none')
    },
    // deep-link from feedback review: jump straight to the single alert
    // record that generated a feedback rating, rather than a free-text search.
    applyDeepLinkQuery() {
      const query = this.$route.query || {}
      if (!query.movement_vehicle_id)
        return
      this.singleAlertFilter = String(query.movement_vehicle_id)
      if (query.alert_type)
        this.alert_type = query.alert_type
      if (query.date) {
        const target = moment(query.date, 'YYYY-MM-DD')
        if (target.isValid()) {
          // pad a day on either side to survive timezone rounding between
          // when the alert was recorded and how the date filter is applied
          this.details_fromDate = target.clone().subtract(1, 'days').format('MM-DD-YYYY')
          this.details_toDate = target.clone().add(1, 'days').format('MM-DD-YYYY')
        }
      }
    },
    clearSingleAlertFilter() {
      this.singleAlertFilter = null
      const query = Object.assign({}, this.$route.query)
      delete query.movement_vehicle_id
      delete query.date
      delete query.alert_type
      this.$router.replace({ query }).catch(() => {})
      this.filter_results()
    },
    async check_features() {
      this.check_cols()
      this.load_data()
    },
    filter_by_type(type) {
      let idx = this.selected_types.indexOf(type)
      if (idx > -1) {
        this.selected_types.splice(idx, 1);
      } else {
        this.selected_types.push(type)
      }
    },
    // falls back to the raw value (with PascalCase/hyphens split into words)
    // for types not covered by type_options, e.g. "MissingDVIR-PRE" -> "Missing DVIR PRE"
    alertTypeLabel(type) {
      const found = this.type_options.find(o => o.value === type)
      if (found) return found.text
      return (type || '')
        .replace(/-/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    },
    hashString(str) {
      let hash = 0
      for (let i = 0; i < str.length; i++) {
        hash = (hash * 31 + str.charCodeAt(i)) | 0
      }
      return Math.abs(hash)
    },
    // per-type hue, picked from whichever palette suits the current theme (see
    // TYPE_CHIP_COLOR_PALETTE_MIDNIGHT). the index is the same in both, so a
    // type keeps its own color when the theme changes.
    typeChipColor(type) {
      const palette = this.$store.state.profile.site_theme === 'midnight'
        ? TYPE_CHIP_COLOR_PALETTE_MIDNIGHT
        : TYPE_CHIP_COLOR_PALETTE
      return palette[this.hashString(type) % palette.length]
    },
    // outline-when-idle / solid-when-selected, mirroring the vsm board's and
    // ops list's colored filter chips. the palette hues sit at ~4.7:1 on their
    // theme's background, so the idle outline's colored label stays readable too.
    // the SELECTED fill stays on the light (deeper) hue in both themes: white on
    // those lands at ~5:1, while the lifted midnight hue would not carry white.
    typeChipStyle(type) {
      const color = this.typeChipColor(type)
      if (this.selected_types.indexOf(type) > -1) {
        const fill = TYPE_CHIP_COLOR_PALETTE[this.hashString(type) % TYPE_CHIP_COLOR_PALETTE.length]
        return { backgroundColor: fill, borderColor: fill, color: '#fff' }
      }
      return { borderColor: color, color }
    },
    // same per-type color as the filter chip, but as bold text for the results table
    typeTextStyle(type) {
      return { color: this.typeChipColor(type), fontWeight: 'bold' }
    },
    // the Message cell holds a different mix per alert type — a voice script, an
    // email subject, the SMS body, or nothing but an HTML email — so each piece
    // present is labeled rather than run together behind inline <b> prefixes.
    messageParts(item) {
      const parts = []

      if (item.voice_msg)
        parts.push(Object.assign({ key: 'voice', label: 'Voice' },
                                 this.messagePreview(item.voice_msg, VOICE_PREVIEW_LIMIT, false)))

      // a subject can be carried on the record from the tenant's template even
      // when the alert only went out by SMS — it's only meaningful once an
      // email actually went somewhere
      if (item.email_subject && this.emailWasSent(item))
        parts.push({ key: 'subject', label: 'Subject', text: item.email_subject, truncated: false })

      // inbound emails land in `message` as the sender's raw body — usually a
      // full HTML document, which previewed verbatim shows markup or (when the
      // cut fell inside <head>) nothing at all. flatten it to the readable text
      // so the column shows what was actually written back.
      if (item.message && item.alert_type === 'EmailReceived') {
        const reply = this.replyWithoutQuotedThread(this.readableBody(item.message))
        const preview = this.messagePreview(reply.text, MESSAGE_PREVIEW_LIMIT, false)
        parts.push(Object.assign({ key: 'received', label: 'Received' }, preview,
                                 { truncated: preview.truncated || reply.trimmed }))
      }
      else if (item.message)
        parts.push(Object.assign({ key: 'sms', label: 'SMS' },
                                 this.messagePreview(item.message, MESSAGE_PREVIEW_LIMIT, true)))

      // a booking state alert's email is an HTML table of the bookings it
      // flagged: preview its readable text (cells spaced apart so ids, times
      // and issues don't run together) and let show more open the full table
      // in the row details
      if (item.alert_type === 'BookingStateAlert' && item.email_message) {
        const text = this.readableBody(item.email_message.replace(/<\/t[dh]>/gi, '  '))
        parts.push(Object.assign({ key: 'email', label: 'Email' },
                                 this.messagePreview(text, MESSAGE_PREVIEW_LIMIT, false)))
      }

      // an HTML-only email has nothing worth previewing inline
      if (item.email_message && !parts.length)
        parts.push({ key: 'email', label: 'Email', text: 'HTML email', truncated: true })

      return parts
    },
    // the expanded row shows the same body the Message column previews, so it
    // gets the same naming — an inbound email isn't an SMS message
    messageBodyLabel(item) {
      return item.alert_type === 'EmailReceived' ? 'received email:' : 'SMS message:'
    },
    // long bodies are cut to a preview; `truncated` drives the show more link,
    // which expands the row details where the untrimmed text already lives
    messagePreview(raw, limit, asHtml) {
      const truncated = raw.length > limit
      const text = truncated ? raw.substring(0, limit) + '…' : raw
      return asHtml
        ? { html: text.replace(/\n/g, '<br/>'), truncated }
        : { text, truncated }
    },
    // a reply carries the whole thread quoted underneath it: everything from the
    // "From:" header (or an Original Message / Outlook divider) down is the mail
    // being replied to, not what the sender wrote. the preview stops there —
    // expanding the row still shows the untouched body. if nothing precedes the
    // header the mail is a bare forward, so keep it rather than show an empty cell.
    replyWithoutQuotedThread(text) {
      const match = QUOTED_THREAD_START.exec(text)
      if (!match) return { text, trimmed: false }
      const reply = text.slice(0, match.index).trim()
      return reply ? { text: reply, trimmed: true } : { text, trimmed: false }
    },
    // HTML body -> the text a person would read: <br>/block ends become line
    // breaks, style and script blocks go away, entities are decoded by the
    // parser. plain-text bodies fall through untouched.
    readableBody(raw) {
      if (!/<[a-z!]/i.test(raw)) return raw
      const withBreaks = raw
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/(p|div|tr|li|h[1-6]|blockquote)>/gi, '\n')
      const doc = new DOMParser().parseFromString(withBreaks, 'text/html')
      doc.querySelectorAll('style, script, head').forEach(node => node.remove())
      return (doc.body ? doc.body.textContent : '')
        .replace(/[ \t\u00a0]+/g, ' ')
        .replace(/ ?\n ?/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim()
    },
    // CM booking ids arrive as "<charterID>_<movementID>" (the API strips the
    // private_hire_/contract_ prefix); split them so the column reads like the
    // actuals page's CharterID + MvmtID instead of one run-together string.
    // TBN/other tenants get a single booking id, which is shown as-is.
    bookingIdParts(item) {
      const raw = String(item.booking_id || '')
      if (!this.isCmTenant || raw.indexOf('_') === -1)
        return { primary: raw, movement: '' }
      const idx = raw.lastIndexOf('_')
      return { primary: raw.slice(0, idx), movement: raw.slice(idx + 1) }
    },
    // Delivery Status column: one row per channel that actually reported, each
    // tagged by channel with the state carrying the color — so a failure reads
    // as a failure without having to parse the raw provider text first.
    deliveryStatuses(item) {
      const rows = []

      if (item.provider_response && !item.provider_response.includes('Message has been accepted'))
        rows.push({ key: 'sms', label: 'SMS', state: 'fail', text: item.provider_response,
                    note: item.phone_carrier ? item.phone_carrier : '' })
      else if (item.mock_send)
        rows.push({ key: 'sms', label: 'SMS', state: 'test', text: 'test — no message sent' })
      else if (item.sms_msg_id)
        rows.push({ key: 'sms', label: 'SMS', state: 'ok', text: 'sent',
                    note: item.retry_attempted ? 'retried' : '' })

      const email = this.getLatestEmailStatus(item)
      if (email) {
        const failed = ['bounce', 'complaint'].includes((email.status || '').toLowerCase())
        rows.push({ key: 'email', label: 'Email', state: failed ? 'fail' : 'ok',
                    text: this.decodeEmailStatus(email.status), datetime: email.datetime })
      }

      return rows
    },
    // the destination fields are sparse — an SMS alert has phone numbers and no
    // email, an email-only alert the reverse — so they share one column and each
    // populated group is labeled instead of getting a column of its own.
    recipientGroups(item) {
      const suppressed = RECIPIENT_GROUPS_HIDDEN_BY_TYPE[this.alert_type] || []
      const split = val => (val || '')
        .split(',')
        .map(s => s.trim())
        .filter(s => s && s.toLowerCase() !== 'none')

      return RECIPIENT_GROUP_DEFS
        .filter(def => suppressed.indexOf(def.key) === -1)
        .map(def => ({ key: def.key, label: def.label, values: split(item[def.field]) }))
        .filter(group => group.values.length)
    },
    // flattened "label value value" text so the merged column can still sort
    recipientSortKey(item) {
      return this.recipientGroups(item)
        .map(group => group.label + ' ' + group.values.join(' '))
        .join(' ')
    },
    async check_cols() {
      this.sms_stats_enabled = this.isEffectiveAdmin
      this.driver_confirmation_enabled = this.user.features[this.user.tenant].driver_trip_notifications_confirmation_enabled
      this.relay_notifications_enabled = this.user.features[this.user.tenant].relay_notifications_enabled
      this.driver_surveys_enabled = this.user.features[this.user.tenant].driver_surveys_enabled
      this.group_message_sender_enabled = this.user.features[this.user.tenant].group_message_sender_enabled
      this.fields = this.fields_all
      if (!this.driver_confirmation_enabled)
        this.fields = this.fields.filter(item => item.key != 'confirmed_time')

      if (!this.sms_stats_enabled)
        this.fields = this.fields.filter(item => (item.key != 'booking_hash' && item.key != 'sms_size' && item.key != 'sms_recipients' && item.key != 'sms_units'))

      // the recipient groups these types don't use are dropped inside the
      // Recipients cell instead — see RECIPIENT_GROUPS_HIDDEN_BY_TYPE
      if (this.alert_type && (this.alert_type == 'SMSReceived' || this.alert_type == 'SMSResponse' || this.alert_type == 'GroupMessage' || this.alert_type == 'DriverHOSAlert'))
        this.fields = this.fields.filter(item => (item.key != 'booking_id' && item.key != 'booking_hash' && item.key != 'vehicle_id'))
    },
    async load_data() {
      this.loading = true
      try {
        const {value: { data = [] } } = await get_alerts(this.user.tenant, this.alert_type, moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD'),
            moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD'))
        this.summary = data.summary
        moment.tz.setDefault(this.summary.timezone)
        this.items = data.alerts
        this.items_full = data.alerts
        this.base_options = data.summary.bases
        // this.type_options = data.summary.alert_types
        this.filter_results()
      } catch(e) {
        this.$emit('showAlert', 'error retrieving data, try logging in again or contact support if needed', 'danger', 5000)
      }
      this.loading = false
    },
    async reprocess_alert(item) {
      if (confirm("Are you sure you want to reprocess and resend notifications for this alert?")) {
        reprocess_alerts_for_booking(this.user.tenant, item.alert_type, item.booking_hash, item.event_name)
        this.$emit('showAlert', 'reprocessing alert', 'info', 5000)
      }
    },
    async copy_alert_id(item) {
      if (!this.isEffectiveAdmin || !item || !item.movement_vehicle_id)
        return
      try {
        await copyToClipboard(String(item.movement_vehicle_id))
        this.copied_alert_id = item.movement_vehicle_id
        if (this.copied_alert_id_timer)
          clearTimeout(this.copied_alert_id_timer)
        this.copied_alert_id_timer = setTimeout(() => {
          this.copied_alert_id = null
        }, 2000)
      } catch (e) {
        this.$emit('showAlert', 'unable to copy alert ID', 'danger', 3000)
      }
    },
    async delete_alert_record(item) {
      if (!this.isEffectiveAdmin) {
        return
      }
      if (!item || !item.movement_vehicle_id) {
        this.$emit('showAlert', 'unable to delete this alert record (missing id)', 'danger', 5000)
        return
      }
      const bookingId = item.booking_id || 'N/A'
      const alertType = item.alert_type || 'Unknown'
      const confirmed = confirm(
        `Delete this alert record?\n\nBooking: ${bookingId}\nType: ${alertType}\n\nThis cannot be undone.`
      )
      if (!confirmed) return
      try {
        await delete_alert(this.user.tenant, item.movement_vehicle_id)
        this.$emit('showAlert', 'alert record deleted', 'success', 5000)
        await this.load_data()
      } catch (e) {
        this.$emit('showAlert', 'failed to delete alert record', 'danger', 5000)
      }
    },
    async download(){
      this.$emit('showAlert', 'processing download request', 'info', 5000)
      axios.defaults.headers.common.Accept = {}
      axios.post(
          `${api_url}bookings/alerts/download`,
          {
            tenant: this.user.tenant,
            start_date: moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD'),
            end_date: moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD'),
            alert_type: this.alert_type,
            alert_filter: this.alert_filter,
            start_base: this.selected_base
          }, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('jwt')}`,
              Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            },
            responseType: 'blob'
          }
      ).then((response) => {
        axios.defaults.headers.common.Accept = 'application/json'
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute(
            'download',
            `alerts-${this.user.tenant}-${this.details_fromDate}.csv`
        )
        document.body.appendChild(link)
        link.click()
        this.$emit('showAlert', 'download complete', 'success', 5000)
      }).catch(() => {
        axios.defaults.headers.common.Accept = 'application/json'
        this.$emit('showAlert', 'alert data could not be downloaded.', 'danger')
      })
    },
    async shift_detail_graph_date(shift) {
      this.details_fromDate = moment(this.details_fromDate, 'MM-DD-YYYY').add(shift, 'days').format('MM-DD-YYYY');
      this.details_toDate = moment(this.details_toDate, 'MM-DD-YYYY').add(shift, 'days').format('MM-DD-YYYY');
      await this.load_data()
    },
    set_days_range(option) {
      this.details_fromDate = justDate(moment().subtract(option, 'days'))
      this.details_toDate = justDate(moment())
      // a preset window is the answer to "custom", so the disclosure closes
      // with it -- otherwise the pickers stay open in mobile mode showing
      // dates the chip beside them already describes
      this.show_custom_dates = false
      this.load_data()
    },
    // active state is derived from the current from/to dates rather than a
    // separately tracked "selected option", so it stays correct whether the
    // range came from a quick button, the date pickers, or the shift arrows
    isActiveRange(option) {
      return this.details_fromDate === justDate(moment().subtract(option, 'days')) &&
        this.details_toDate === justDate(moment())
    },
    toggle_div(item,field) {
      item[field] = !item[field];
      this.$forceUpdate();
    },
    get_row_color(item, type) {
      if (!item || type !== 'row') return
      return item.status
    },
    filter_results() {
      this.items = this.items_full
      if (this.singleAlertFilter) {
        this.items = this.items.filter(item => String(item.movement_vehicle_id) === this.singleAlertFilter)
        this.items_for_issue_counts = this.items
        this.items_for_feedback_counts = this.items
        return
      }
      if (this.selected_types.length > 0)
        this.items = this.items.filter(item => (this.selected_types.indexOf(item.alert_type) != -1))
      if (this.alert_type != '')
        this.items = this.items.filter(item => (this.contains(item.alert_type,this.alert_type)))
      if (this.confirmed_state != '')
        if (this.confirmed_state != 'confirmed')
          this.items = this.items.filter(item => (item.confirmed_time == null))
        else if (this.confirmed_state != 'unconfirmed')
          this.items = this.items.filter(item => (item.confirmed_time != null))
      if (this.alert_filter != '') {
        const val = this.alert_filter
        this.items = this.items.filter(item => (this.contains(item.provider_response, val) || this.contains(item.message, val) || this.contains(item.start_base, val) || this.contains(item.booking_id, val) || this.contains(item.email, val) || this.contains(item.sms_leaders, val) || this.contains(item.sms_drivers, val) || this.contains(item.sms, val) || this.contains(item.sms_name, val) || this.contains(item.vehicle_id, val)))
      }
      if (this.selected_base) {
        this.items = this.items.filter(item => this.contains(item.start_base,this.selected_base))
      }
      // the Issues chip count reflects every other active filter, so snapshot
      // here before the issues filter itself narrows this.items further
      this.items_for_issue_counts = this.items
      if (this.needs_attention) {
        this.items = this.items.filter(item => this.hasDeliveryIssue(item));
      }
      // feedback chip counts reflect every other active filter, so snapshot
      // here before the feedback filter itself narrows this.items further
      this.items_for_feedback_counts = this.items
      if (this.selected_feedback != null && this.feedback_audit_mode_enabled) {
        this.items = this.items.filter(item => matchesAlertFeedbackFilter(item, this.selected_feedback))
      }
    },
    filter_by_feedback(feedback) {
      if (feedback == null || this.selected_feedback === feedback) {
        this.selected_feedback = null
      } else {
        this.selected_feedback = feedback
      }
      this.currentPage = 1
      this.filter_results()
    },
    async confirm_clear_feedback_for_date_range() {
      if (this.clearing_feedback || !this.feedback_audit_mode_enabled)
        return
      const fromDate = moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
      const toDate = moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
      const ratedCount = this.feedback_positive_cnt + this.feedback_negative_cnt + this.feedback_concern_cnt
      const rangeLabel = `${this.details_fromDate} to ${this.details_toDate}`
      const msg = ratedCount > 0
        ? `Delete feedback on ${ratedCount} alerts from ${rangeLabel}?`
        : `Delete all alert feedback from ${rangeLabel}?`
      if (!confirm(`${msg} This cannot be undone from the Alert History page.`))
        return
      this.clearing_feedback = true
      try {
        const {value: {data = {}}} = await clear_alert_feedback_for_date_range(this.user.tenant, fromDate, toDate)
        const deleted = data.deleted_count || 0
        this.selected_feedback = null
        this.clear_feedback_from_loaded_rows()
        this.$emit('showAlert', `Cleared feedback on ${deleted} alerts`, 'success', 4000)
        if (deleted > 0)
          await this.load_data()
      } catch (e) {
        this.$emit('showAlert', 'error clearing feedback, try again or contact support', 'danger', 5000)
      } finally {
        this.clearing_feedback = false
      }
    },
    clear_feedback_from_loaded_rows() {
      for (const item of this.items_full || []) {
        this.$delete(item, 'feedback')
        this.$delete(item, 'feedback_comment')
      }
    },
    contains(val1, val2) {
      if (val1 != null) {
        return val1.toLowerCase().indexOf(val2.toLowerCase()) != -1;
      }
      return false;
    },

    // a failed SMS or a bounced/complaint email — what the Issues chip filters on
    hasDeliveryIssue(item) {
      const emailStatus = this.getLatestEmailStatus(item)
      const hasEmailIssue = emailStatus &&
        ['bounce', 'complaint'].includes((emailStatus.status || '').toLowerCase())
      const hasSmsIssue = item.provider_response &&
        !item.provider_response.includes('Message has been accepted')
      return !!(hasEmailIssue || hasSmsIssue)
    },

    // an email went out if the alert has addresses on it (what the summary's
    // "Emails Sent" counts) or the provider reported back on one
    emailWasSent(item) {
      return !!((item.email && item.email.trim()) || this.getLatestEmailStatus(item))
    },

    getLatestEmailStatus(item) {
      if (!item.email_msg_status || !item.email_msg_status.length) return null;
      return item.email_msg_status.reduce((a, b) =>
        new Date(a.datetime) > new Date(b.datetime) ? a : b
      );
    },

    decodeEmailStatus(rawStatus) {
      if (!rawStatus) return ''
      const map = {
        send:      'sent',
        delivery:  'delivered',
        bounce:    'bounced',
        complaint: 'filed complaint',
        open:      'opened',
        click:     'clicked link',
      }
      return map[rawStatus.toLowerCase()] || rawStatus
    },
  },
  computed:{
    ...mapState({
      user: state => state.profile.user,
      viewAs: state => state.profile.view_as,
    }),
    isEffectiveAdmin() {
      return this.user.role === 'admin' && this.viewAs !== 'non-admin'
    },
    isCmTenant() {
      return this.user.types[this.user.tenant] === 'CM'
    },
    dateRangeOptions() {
      return DATE_RANGE_OPTIONS
    },
    // the window matches none of the range chips -- set by the pickers, by the
    // shift arrows, or by an inbound query string
    isCustomRange() {
      return !DATE_RANGE_OPTIONS.some(option => this.isActiveRange(option))
    },
    // whether the From/To cluster shows in mobile mode: either the user asked
    // for it, or the window is already bespoke and hiding the dates that
    // produced it would leave the row claiming no selection at all
    datesOpen() {
      return this.show_custom_dates || this.isCustomRange
    },
    // the native date inputs speak ISO; the rest of the page (and the api
    // calls) keep the legacy MM-DD-YYYY strings, so these adapt at the edge.
    // an empty value (the input's clear affordance) is ignored rather than
    // propagated -- this page always queries a bounded window.
    fromDateIso: {
      get() { return moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD') },
      set(v) { if (v) this.details_fromDate = moment(v, 'YYYY-MM-DD').format('MM-DD-YYYY') },
    },
    toDateIso: {
      get() { return moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD') },
      set(v) { if (v) this.details_toDate = moment(v, 'YYYY-MM-DD').format('MM-DD-YYYY') },
    },
    hasTypeChips() {
      return !!(this.summary && this.summary.type_unit_counts && Object.keys(this.summary.type_unit_counts).length)
    },
    // b-table slices `items` by current-page/per-page itself; the card list
    // has to do its own, so both branches answer the same pagination control
    pagedItems() {
      const start = (this.currentPage - 1) * this.perPage
      return this.items.slice(start, start + this.perPage)
    },
    // length of the current from/to window in days, so the double-arrow
    // shift moves by "one more of these" instead of a fixed step
    selectedDayCount() {
      const diff = moment(this.details_toDate, 'MM-DD-YYYY').diff(moment(this.details_fromDate, 'MM-DD-YYYY'), 'days')
      return diff > 0 ? diff : 1
    },
    // where the booking id links to for this tenant type
    actualsPath() {
      const type = this.user.types[this.user.tenant]
      if (type === 'CM') return '/actuals'
      if (type === 'TBN') return '/actuals_tbn'
      return '/actuals_fasttrak'
    },
    tenantFeatures() {
      return (this.user.features && this.user.features[this.user.tenant]) || {}
    },
    showEnableAlertsPackageButton() {
      return hasVsmAccess(this.tenantFeatures) && !hasAlertsAccess(this.tenantFeatures)
    },
    feedback_audit_mode_enabled() {
      return isFeedbackAuditModeEnabled(this.user)
    },
    feedback_positive_cnt() {
      return countAlertFeedback(this.items_for_feedback_counts, 'positive')
    },
    feedback_negative_cnt() {
      return countAlertFeedback(this.items_for_feedback_counts, 'negative')
    },
    feedback_concern_cnt() {
      return countAlertFeedback(this.items_for_feedback_counts, 'concern')
    },
    feedback_none_cnt() {
      return countAlertFeedback(this.items_for_feedback_counts, 'none')
    },
    // rows with a delivery problem among everything the other filters let
    // through — what toggling the Issues chip would narrow the table to
    issues_cnt() {
      return (this.items_for_issue_counts || []).filter(item => this.hasDeliveryIssue(item)).length
    },
  },
  watch: {
    user: {
      deep: true,
      handler() {
        this.check_features()
      }
    },
    alert_type() {
      this.check_cols()
      this.filter_results();
    },
    confirmed_state() {
      this.filter_results()
    },
    alert_filter() {
      this.filter_results()
    },
    needs_attention() {
      this.filter_results();
    },
    selected_base() {
      this.filter_results()
    },
    selected_types() {
      this.filter_results()
    },
    details_fromDate(target_from_date) {
      this.details_fromDate = target_from_date
      if (moment(this.details_fromDate, 'MM-DD-YYYY') > moment(this.details_toDate, 'MM-DD-YYYY'))
        this.details_toDate = this.details_fromDate
    }
  }
}
</script>

<style>
/* ── page scaffold + header rail: same construction as alerts_dashboard ── */
.al-page {
  /* rail and table bleeds derive from this, so narrower breakpoints stay the
     same 1px off the edge instead of snapping to a wider inset */
  --al-gutter: 20px;
  padding: 12px var(--al-gutter) 32px;
}

@media (max-width: 720px) {
  .al-page {
    --al-gutter: 16px;
    padding: 12px var(--al-gutter) 24px;
  }
}

.al-rail {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  background: var(--fi-ops-rail-bg);
  border-left: 3px solid var(--fi-rail-accent);
  border-radius: 0 6px 6px 0;
  padding: 12px 16px;
  margin-bottom: 14px;
}

/* bleed to the page edge at every width, mirroring .od-rail--flush */
.al-rail--flush {
  margin: -11px calc(1px - var(--al-gutter)) 14px;
}

/* scales with the viewport rather than stepping at a breakpoint: the rail now
   carries the Issues chip and the download alongside the page actions in
   mobile mode, and a fixed 22px title pushed that cluster onto a second line.
   the clamp holds the full 22px from ~650px of viewport up, and eases to
   ~16px at the narrowest phone -- still the largest text on the page. */
.al-rail-title {
  margin: 0;
  font-size: clamp(16px, 2vw + 9px, 22px);
  line-height: 1.2;
  font-weight: 700;
  letter-spacing: -0.1px;
  color: var(--fi-text);
  /* it is the elastic thing in the rail: the actions beside it are icons at
     fixed sizes, so the title gives up room first rather than wrapping them */
  min-width: 0;
}

.al-rail-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* ── filters: the ops_desk chip + filter-row language ──────────────────── */
.al-chip-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 14px;
  margin-bottom: 12px;
}

.al-chip-group {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}

/* dissolves the group's box (its role/aria wrapper stays in the markup) so
   the chips join the parent row's wrap flow: siblings after the group — the
   Issues chip, the confirmed select — can fill the trailing space of a
   wrapped chip line instead of being pushed to a fresh line by an atomic
   nested flexbox. spacing comes from the row's gap once the box is gone. */
.al-chip-group--flow {
  display: contents;
}

/* mobile mode: a tenant with a dozen alert types wrapped this group to four
   or five lines of chips, pushing the results off the first screen. the group
   takes its box back here and becomes one line that scrolls sideways -- and
   claims a full row, so the Issues chip and the Confirmed select wrap under
   it rather than being dragged into the scroller.
   900px matches setupMobileQuery()'s media query, which is what swaps the
   table for the card list; keep the two in step. */
@media (max-width: 900px) {
  .al-chip-group--flow {
    display: flex;
    flex: 1 1 100%;
    flex-wrap: nowrap;
    align-items: center;
    gap: 6px;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    /* the chips' focus ring is drawn outside their box and would be clipped
       by the scroller on the first and last chip */
    padding: 2px;
    margin: -2px;
  }

  .al-chip-group--flow::-webkit-scrollbar {
    display: none;
  }

  /* chips keep their natural width inside the scroller instead of being
     squeezed to fit the viewport */
  .al-chip-group--flow > .al-chip {
    flex: 0 0 auto;
  }

  .al-chip-group--flow > .al-chip-group-label {
    flex: 0 0 auto;
  }
}

.al-chip-group-label {
  font-size: 10.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #6b7280;
  margin-right: 2px;
}

.al-chip {
  background: #f8fafc;
  border: 1px solid #d6dde6;
  border-radius: 999px;
  padding: 4px 12px;
  font-size: 12.5px;
  font-weight: 600;
  color: #4b5563;
  cursor: pointer;
  transition: background .12s, border-color .12s, color .12s;
  white-space: nowrap;
}

/* neutral chips keep the plain wash on hover; colored variants below own
   their hover so the wash can't strip the hue */
.al-chip:hover:not(:disabled):not(.al-chip--active):not(.al-chip--type):not([class*="al-fc-"]) {
  background: #eef4fb;
  border-color: #b8c8da;
}

.al-chip:disabled {
  cursor: default;
  opacity: 0.6;
}

.al-chip--active {
  background: var(--fi-ops-accent);
  border-color: var(--fi-ops-accent);
  color: #fff;
}

/* colored variants: a clear colored outline when idle, a solid fill of the
   same hue when selected (the od-fc / vb-fc voice). type chips get their hue
   from an inline style (the type set is open-ended); feedback chips are
   static classes. keep the active rules after these: same specificity, so
   source order is what lets the active fill win. */
.al-chip--type,
.al-chip[class*="al-fc-"] {
  background: #fff;
  border-width: 1.5px;
}

.al-chip--type:not(.al-chip--active):hover,
.al-chip[class*="al-fc-"]:not(.al-chip--active):hover {
  background: #f4f7fa;
}

/* denser than the base chip so the open-ended type set packs more per line */
.al-chip--type {
  padding: 2px 9px;
  font-size: 11.5px;
}

.al-fc-green { border-color: #1fab42; color: #146230; }
.al-fc-red   { border-color: #d62f2f; color: #b22525; }
.al-fc-amber { border-color: #d39410; color: #7e5407; }
.al-fc-gray  { border-color: #6b7280; color: #4b5563; }

.al-fc-green.al-chip--active { background: #1fab42; border-color: #1fab42; color: #15202b; }
.al-fc-red.al-chip--active   { background: #d62f2f; border-color: #d62f2f; color: #fff; }
.al-fc-amber.al-chip--active { background: #d39410; border-color: #d39410; color: #15202b; }
.al-fc-gray.al-chip--active  { background: #6b7280; border-color: #6b7280; color: #fff; }

.al-chip:focus-visible {
  outline: 2px solid var(--fi-ops-accent);
  outline-offset: 2px;
}

.al-filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 10px;
  margin-bottom: 10px;
  font-size: 13px;
}

/* the refine row closes the filter zone with the ops list's hairline. its
   gap is tighter than the base filter row's: the row is mostly type chips
   (their group box dissolved via display:contents, so this container gap IS
   the chip spacing) and the tighter run packs more chips per line */
.al-filter-row--refine {
  gap: 6px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eef0f3;
  margin-bottom: 8px;
}

.al-filter-field {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

/* one-line date cluster; only true mobile splits it, and then each half
   keeps its flanking arrows */
.al-filter-dates {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 8px 10px;
}

.al-filter-dateunit {
  display: inline-flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 8px;
}

/* the Custom disclosure only exists where the row runs out of width; above
   the mobile breakpoint the From/To cluster is simply always shown */
.al-chip--custom {
  display: none;
}

/* narrower than the mobile breakpoint below, and older than it: the point
   where the two halves of the date cluster stop fitting on one line */
@media (max-width: 600px) {
  .al-filter-dates {
    flex-wrap: wrap;
  }
}

/* mobile mode -- keyed to setupMobileQuery()'s 900px, the same width at which
   the results table gives way to the card list */
@media (max-width: 900px) {
  .al-chip--custom {
    display: inline-block;
    /* an ellipsis glyph in a chip sized for "30d" -- the padding is what
       keeps it a tap target rather than the label */
    padding: 4px 10px;
    letter-spacing: 1px;
  }

  /* the type select joins the range line instead of taking one of its own:
     the chips keep their natural width and the select flexes into the rest.
     the :not() matters -- the types group is also a direct child of a filter
     row, and it claims a full row of its own as a scroller (above). */
  .al-filter-row > .al-chip-group:not(.al-chip-group--flow) {
    flex: 0 0 auto;
  }

  .al-filter-field--type {
    flex: 1 1 110px;
    min-width: 0;
  }

  .al-filter-field--type .al-chip-group-label {
    display: none;
  }

  .al-filter-row .al-filter-field--type .al-filter-select--wide {
    width: 100%;
    min-width: 0;
  }

  /* collapsed by default: the pickers plus four shift arrows are the widest
     thing on the page, and most visits never leave the range chips */
  .al-filter-dates {
    display: none;
  }

  .al-filter-dates--open {
    display: flex;
    /* its own line under the chips rather than squeezed beside them, so the
       two date fields keep a usable width */
    flex: 1 1 100%;
  }
}

.al-filter-row .al-filter-input {
  width: 150px;
  font-size: 13px;
}

.al-filter-row .al-filter-select,
.al-results-meta .al-filter-select {
  width: 160px;
  font-size: 13px;
}

.al-filter-row .al-filter-select--wide {
  width: 230px;
}

.al-filter-row .toolbar-date-shift-btn {
  padding: 0.15rem 0.35rem;
  line-height: 1;
  min-width: 1.65rem;
}

.al-filter-row .toolbar-date-shift-btn .fa {
  font-size: 13px;
  font-weight: bold;
  vertical-align: middle;
}

/* the download closes the filter row on the far right (the search pill used
   to carry the auto margin before it moved to the results meta line) */
.al-filter-row .al-download {
  margin-left: auto;
}

/* base + search close the meta line on the right: the auto margin rides the
   base select when the tenant has one, else the search pill keeps its own */
.al-results-meta .al-meta-base {
  margin-left: auto;
}

.al-meta-base + .al-search {
  margin-left: 0;
}

/* search pill, right-aligned in the results meta line (the vsm board's .vb-search) */
.al-search {
  margin-left: auto;
  position: relative;
  display: inline-flex;
  align-items: center;
  flex: 0 1 240px;
  min-width: 170px;
}

.al-search-icon {
  position: absolute;
  left: 11px;
  color: #9aa3ad;
  font-size: 12px;
  pointer-events: none;
}

.al-search-input {
  width: 100%;
  padding: 5px 26px 5px 28px;
  font-size: 13px;
  border: 1px solid #d6dde6;
  border-radius: 999px;
  background: #fff;
  color: #1d1d1f;
  transition: border-color .12s, box-shadow .12s;
}

.al-search-input::placeholder { color: #9aa3ad; }

.al-search-input:focus {
  outline: none;
  border-color: var(--fi-ops-accent);
  box-shadow: 0 0 0 2px rgba(58, 110, 153, 0.2);
}

.al-search-clear {
  position: absolute;
  right: 6px;
  background: none;
  border: none;
  padding: 2px 5px;
  color: #9aa3ad;
  cursor: pointer;
  font-size: 12px;
  line-height: 1;
}

.al-search-clear:hover { color: #4b5563; }

/* results meta line replacing the grey summary boxes (vsm results-count voice) */
.al-results-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
  margin-bottom: 10px;
  font-size: 12.5px;
  color: #6b7a8c;
}

/* transparent on desktop: the counts sit in the meta line's own flow exactly
   as they did before they were grouped */
.al-results-stats {
  display: contents;
}

.al-results-count {
  font-weight: 600;
  color: #3d4b5c;
}

.al-results-stat::before {
  content: '·';
  margin-right: 8px;
  color: #b6bcc4;
}

/* midnight ink for the meta line; the data-fi-theme attribute lives on <html>,
   which this unscoped block can reach */
[data-fi-theme='midnight'] .al-results-meta { color: #8fa3b5; }

/* mobile mode (setupMobileQuery()'s 900px): the counts take their box back and
   become one sideways-scrolling line. four stats plus a dot separator each
   wrapped to three lines on a phone, which pushed the first card down a third
   of the screen. the base select and search pill keep their own line below,
   where they stay tappable rather than being buried in a scroller. */
@media (max-width: 900px) {
  .al-results-stats {
    display: flex;
    flex: 1 1 100%;
    flex-wrap: nowrap;
    align-items: center;
    gap: 8px;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    white-space: nowrap;
  }

  .al-results-stats::-webkit-scrollbar {
    display: none;
  }

  .al-results-stats > span {
    flex: 0 0 auto;
  }

  /* their auto margins only mean "close the line on the right", which is not
     what a full-width row below the counts wants */
  .al-results-meta .al-meta-base,
  .al-results-meta .al-search {
    margin-left: 0;
  }

  .al-results-meta .al-search {
    flex: 1 1 auto;
  }
}
[data-fi-theme='midnight'] .al-results-count { color: #d7e1ea; }
[data-fi-theme='midnight'] .al-results-stat::before { color: #55677a; }

.al-single-alert-banner {
  margin: 0 0 12px;
  padding: 8px 14px;
  background: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 8px;
  font-size: 14px;
  font-weight: bold;
  text-align: center;
}

/* the results table bleeds to the page edge at every width, like .od-table-wrap */
.al-table-wrap {
  margin-left: calc(1px - var(--al-gutter));
  margin-right: calc(1px - var(--al-gutter));
}

/* the rail's own copy of the Issues chip: tighter than the filter-row chip
   it stands in for, since it sits beside 30px icon buttons */
.al-rail-issues {
  padding: 3px 9px;
  font-size: 12px;
  line-height: 1.4;
}

/* ── mobile card list (the vsm board's .vb-card-list construction) ─────── */
.al-card-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0 var(--al-gutter);
  text-align: left;
}

.al-card-busy,
.al-card-empty {
  padding: 18px 4px;
  text-align: center;
  color: var(--fi-text-muted);
  font-size: 13px;
}

/* transparent rather than a painted surface: midnight paints the page dark
   and a white card would be a lit box on it. the border and the left accent
   are what make the card read as a card, and both come from tokens that
   already answer per theme. */
.al-acard {
  border: 1px solid var(--fi-line);
  border-left: 3px solid var(--fi-line-strong);
  border-radius: 8px;
  background: transparent;
  padding: 10px 12px;
  font-size: 13px;
  color: var(--fi-text);
}

/* the one row state this page cares about: a failed SMS or a bounced email,
   the same test the Issues filter runs. it earns the accent because it is
   what sends someone to this page in the first place. */
.al-acard--issue {
  border-left-color: var(--fi-danger);
}

.al-acard-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}

/* the type chip wears the hue the table column assigns it (typeChipStyle),
   so a type is the same color in either layout */
.al-acard-type {
  padding: 2px 9px;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 700;
  white-space: nowrap;
}

.al-acard-time {
  flex: none;
  font-size: 11.5px;
  color: var(--fi-text-muted);
  white-space: nowrap;
}

.al-acard-reprocessed {
  font-style: italic;
}

.al-acard-ident {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 12px;
  margin-top: 7px;
  font-size: 12.5px;
}

/* the card has no column headers, so each identity value carries the icon
   that says which column it came from */
.al-acard-ident-icon {
  margin-right: 4px;
  color: var(--fi-text-faint);
  font-size: 11px;
}

.al-acard-booking {
  font-weight: 700;
}

.al-acard-section {
  margin-top: 8px;
}

/* a collapsed card tightens the shared ten-line ceiling to three: alert bodies
   run to a paragraph or two, and at ten lines one card could still fill a
   phone screen on its own. expanding the card returns it to the ten every
   other surface uses, and adds the unclamped full body below it. */
.al-acard-message--clamped .al-message-text {
  -webkit-line-clamp: 3;
  line-clamp: 3;
}

.al-acard-details {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid var(--fi-line);
}

.al-acard-foot {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid var(--fi-line);
}

.al-acard-action {
  cursor: pointer;
  font-size: 15px;
  line-height: 1;
  color: var(--fi-text-muted);
}

.al-acard-action--danger {
  font-weight: 700;
  color: var(--fi-danger);
}

/* the card's stand-in for the table's expand column: pushed to the trailing
   edge so the feedback controls keep the leading one they have in the table */
.al-acard-expand {
  margin-left: auto;
  border: 1px solid var(--fi-line-strong);
  border-radius: 999px;
  background: transparent;
  padding: 3px 11px;
  font-size: 12px;
  font-weight: 600;
  color: var(--fi-text-muted);
}

/* shared label pill for the Recipients / Delivery Status / Message columns:
   these cells hold a different mix of content per alert type, so each piece is
   tagged with what it is instead of relying on column position */
.al-tag {
  justify-self: start;
  padding: 1px 6px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  white-space: nowrap;
}

/* soft tints — these label the row, they shouldn't outshout the values */
.al-tag-name { background: #eceff1; color: #4a545c; }
.al-tag-driver { background: #e2ecf7; color: #2b5f8c; }
.al-tag-customer { background: #e4f0e6; color: #2f6b41; }
.al-tag-other { background: #f0ecf6; color: #5b4a7a; }
.al-tag-voice { background: #f4ecdd; color: #7a5c22; }
.al-tag-email { background: #e0f0ef; color: #26645f; }
.al-tag-sms { background: #e2ecf7; color: #2b5f8c; }
.al-tag-subject { background: #eceff1; color: #4a545c; }
.al-tag-received { background: #e0f0ef; color: #26645f; }

/* merged Recipients column: one labeled row per populated destination group */
.al-recipients {
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: left;
  font-size: 13px;
  line-height: 1.35;
}

.al-recipient-group {
  display: grid;
  grid-template-columns: 62px minmax(0, 1fr);
  gap: 2px 6px;
  align-items: start;
}

.al-recipient-values {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

/* long lists stay scannable without stretching the row */
.al-recipient-values-scroll {
  max-height: 84px;
  overflow-y: auto;
}

.al-recipient-value {
  overflow-wrap: anywhere;
}

/* Booking: charter id leads, movement id sits under it as a sub-line */
.al-booking-id {
  display: inline-flex;
  flex-direction: column;
  line-height: 1.25;
}

.al-booking-id-primary {
  font-weight: bold;
}

.al-booking-id-mvmt {
  font-size: 13px;
  font-weight: normal;
  opacity: 0.85;
}

/* Delivery Status: one row per channel, state carries the color */
.al-delivery {
  display: flex;
  flex-direction: column;
  gap: 5px;
  text-align: left;
  font-size: 13px;
  line-height: 1.35;
}

.al-delivery-row {
  display: grid;
  grid-template-columns: 46px minmax(0, 1fr);
  gap: 2px 6px;
  align-items: start;
}

.al-delivery-body {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.al-delivery-text {
  overflow-wrap: anywhere;
}

.al-delivery-ok { color: #1f7a3d; font-weight: 600; }
.al-delivery-fail { color: #b3261e; font-weight: 700; }
.al-delivery-test { color: #6c757d; font-weight: 600; }

.al-delivery-note {
  font-size: 11px;
  color: #6c757d;
}

/* Message: voice script / subject / body each labeled, long text previewed */
.al-message {
  display: flex;
  flex-direction: column;
  gap: 5px;
  text-align: left;
  font-size: 13px;
  line-height: 1.4;
}

.al-message-part {
  display: grid;
  grid-template-columns: 56px minmax(0, 1fr);
  gap: 2px 6px;
  align-items: start;
}

.al-message-body {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

/* plain-text parts (voice script, flattened inbound email) keep their own line
   breaks; the html part already carries <br/> and wraps normally.

   ten rendered lines is the ceiling at every width. the character cap the
   previews already carry (MESSAGE_PREVIEW_LIMIT) bounds the text, not its
   height: a body of short lines -- an itinerary, a stop list -- stays well
   inside 400 characters and still ran a dozen-plus rows deep, which in the
   table pushed the next row off screen. nothing becomes unreachable: the
   untruncated body is in the expanded row / card detail, which is not
   clamped. */
.al-message-text {
  white-space: pre-wrap !important;
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-line-clamp: 10;
  line-clamp: 10;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.al-message-mono {
  white-space: normal !important;
  font-family: monospace;
}

.al-message-more {
  align-self: flex-start;
  margin-top: 2px;
  font-size: 11px;
}

.al-actions-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
}

.error { background-color: rgba(255,0,0,0.25)}
.success { background-color: hsla(120,100%,75%,0.3)}
.unprocessed { background-color: hsla(200,40%,80%,0.3) }
.cancelled { background-color: hsla(290,60%,70%,0.3) }

.error td { background-color: rgba(255,0,0,0.25)}
.success td {background-color: hsla(120,100%,75%,0.3)}
.unprocessed td { background-color: hsla(200,40%,80%,0.3) }
.cancelled td { background-color: hsla(290,60%,70%,0.3) }


div.row:nth-child(even) {
  background-color: #f2f2f2;
}

tr:nth-child(even) {
  background-color: #f2f2f2;
}

.header {
  font-weight: bold;
}

div.col {
  border: 1px solid grey;
}

.table td {
  padding: .1rem !important;
}

.mvmt-table td {
  vertical-align: middle !important;
}

div.wrap {
  border: 1px solid grey;
  flex-wrap: wrap;
  display: flex;
  inline-size: 150px;
  /* overflow-wrap: break-word; */
}

recipients-list {
  white-space: break-spaces !important;
}

</style>

